import apiClient from './apiClient';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export type ReportType = 'fake_listing' | 'abuse' | 'scam' | 'animal_cruelty' | 'spam' | 'inappropriate_content' | 'other';
export type ReportStatus = 'pending' | 'reviewing' | 'resolved' | 'dismissed' | 'escalated';
export type ReportAction = 'post_deleted' | 'user_warned' | 'temporary_ban' | 'permanent_ban' | 'no_action' | 'pending';

export interface Report {
  _id: string;
  reporter: {
    _id: string;
    name: string;
    username: string;
    email?: string;
    profileImage?: string;
  };
  reportedUser?: {
    _id: string;
    name: string;
    username: string;
    email?: string;
    profileImage?: string;
  } | null;
  reportedPost?: {
    _id: string;
    caption?: string;
    media?: any[];
    user?: any;
  } | null;
  reportedPet?: {
    _id: string;
    title?: string;
    images?: string[];
    owner?: any;
  } | null;
  type: ReportType;
  reason: string;
  description?: string;
  status: ReportStatus;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assignedTo?: {
    _id: string;
    name: string;
    username: string;
  } | null;
  actionTaken?: ReportAction;
  actionNotes?: string;
  resolvedBy?: {
    _id: string;
    name: string;
    username: string;
  } | null;
  resolvedAt?: string | null;
  evidence?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ReportFilters {
  status?: ReportStatus;
  type?: ReportType;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  assignedTo?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ReportStats {
  statusStats: Array<{ _id: string; count: number }>;
  typeStats: Array<{ _id: string; count: number }>;
  priorityStats: Array<{ _id: string; count: number }>;
  pendingCount: number;
  urgentCount: number;
}

class ReportService {
  async getReports(filters: ReportFilters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await apiClient.get<ApiResponse<{ reports: Report[]; pagination: any }>>(
      `/reports${params.toString() ? '?' + params.toString() : ''}`
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch reports');
    }
    return response.data.data;
  }

  async getReportById(id: string) {
    const response = await apiClient.get<ApiResponse<Report>>(`/reports/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch report');
    }
    return response.data.data;
  }

  async updateReportStatus(id: string, status: ReportStatus, assignedTo?: string | null) {
    const response = await apiClient.patch<ApiResponse<Report>>(`/reports/${id}/status`, {
      status,
      assignedTo
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update report status');
    }
    return response.data.data;
  }

  async takeAction(id: string, action: ReportAction, actionNotes?: string, userId?: string, postId?: string) {
    const response = await apiClient.post<ApiResponse<Report>>(`/reports/${id}/action`, {
      action,
      actionNotes,
      userId,
      postId
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to take action on report');
    }
    return response.data.data;
  }

  async getReportStats() {
    const response = await apiClient.get<ApiResponse<ReportStats>>('/reports/stats');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch report statistics');
    }
    return response.data.data;
  }
}

export default new ReportService();

